import React, { useEffect, useState } from 'react';
import { useJWTAuth } from '../JWTAuthContext';
import { getStudyStreak, getGoalsProgress } from '../apiClient';

interface HeroWelcomeProps {
  userStats?: {
    tests_completed: number;
    overall_progress: number;
  };
}

const HeroWelcome: React.FC<HeroWelcomeProps> = ({ userStats }) => {
  const { user } = useJWTAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [studyStreak, setStudyStreak] = useState(0);
  const [dailyGoal, setDailyGoal] = useState({ target: 3, completed: 0, percentage: 0 });
  const [weeklyProgress, setWeeklyProgress] = useState({ current: 0, target: 15, percentage: 0 });
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Fetch study streak
    getStudyStreak()
      .then(response => {
        if (response && response.result) {
          setStudyStreak(response.result.current_streak || 0);
        }
      })
      .catch(error => console.error('Failed to fetch study streak:', error));

    // Fetch goals progress
    getGoalsProgress()
      .then(response => {
        if (response && response.result) {
          const { daily, weekly } = response.result;
          setDailyGoal({
            target: daily.target,
            completed: daily.completed,
            percentage: daily.percentage
          });
          setWeeklyProgress({
            current: weekly.completed,
            target: weekly.target,
            percentage: weekly.percentage
          });
        }
      })
      .catch(error => console.error('Failed to fetch goals progress:', error));
  }, []);

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return { text: 'Good Morning', emoji: '🌅', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' };
    if (hour < 17) return { text: 'Good Afternoon', emoji: '☀️', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' };
    return { text: 'Good Evening', emoji: '🌙', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' };
  };

  const greeting = getGreeting();
  const userName = user?.name?.split(' ')[0] || 'Student';

  return (
    <div className="hero-welcome-section mb-4">
      <div className="card border-0 shadow-lg overflow-hidden" 
        style={{ 
          borderRadius: '20px',
          background: greeting.gradient,
          color: 'white',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        onClick={() => setIsExpanded(!isExpanded)}>
        <div className="card-body p-3">
          <div className="row align-items-end">
            {/* Left Section - Greeting */}
            <div className="col-lg-8">
              <div className="mb-0">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h1 className="display-5 fw-bold mb-2 animate__animated animate__fadeInLeft">
                      {greeting.text}, {userName}! {greeting.emoji}
                    </h1>
                    <p className="lead mb-0" style={{ opacity: 0.95 }}>
                      {currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                  <div className="ms-3 up-down-icon">
                    <div 
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: '40px',
                        height: '40px',
                        background: 'rgba(255, 255, 255, 0.25)',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <i 
                        className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} fa-lg`}
                        style={{ 
                          opacity: 0.9,
                          transition: 'transform 0.3s ease'
                        }}
                      ></i>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expandable Content */}
              {isExpanded && (
                <div className="animate__animated animate__fadeIn">
                  {/* Study Streak */}
                  <div className="d-flex align-items-center mb-4 p-3 rounded-3" 
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)'
                    }}>
                    <div className="me-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: '60px',
                          height: '60px',
                          background: 'rgba(255, 255, 255, 0.25)',
                          fontSize: '28px'
                        }}>
                        🔥
                      </div>
                    </div>
                    <div>
                      <div className="h4 fw-bold mb-0">{studyStreak} Day Streak!</div>
                      <small style={{ opacity: 0.9 }}>Keep it up! You're on fire 🚀</small>
                    </div>
                  </div>

                  {/* Daily & Weekly Goals */}
                  <div className="row g-3">
                <div className="col-md-6">
                  <div className="p-3 rounded-3" 
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)'
                    }}>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fw-semibold">📝 Today's Goal</span>
                      <span className="badge bg-white text-primary px-3">
                        {dailyGoal.completed}/{dailyGoal.target}
                      </span>
                    </div>
                    <div className="progress" style={{ height: '8px', background: 'rgba(255, 255, 255, 0.25)' }}>
                      <div 
                        className="progress-bar bg-white" 
                        style={{ width: `${dailyGoal.percentage}%` }}
                        role="progressbar"
                      ></div>
                    </div>
                    <small className="d-block mt-2" style={{ opacity: 0.9 }}>
                      {dailyGoal.target - dailyGoal.completed} quiz{dailyGoal.target - dailyGoal.completed !== 1 ? 'zes' : ''} to go!
                    </small>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="p-3 rounded-3" 
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)'
                    }}>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fw-semibold">📅 This Week</span>
                      <span className="badge bg-white text-success px-3">
                        {weeklyProgress.current}/{weeklyProgress.target}
                      </span>
                    </div>
                    <div className="progress" style={{ height: '8px', background: 'rgba(255, 255, 255, 0.25)' }}>
                      <div 
                        className="progress-bar bg-white" 
                        style={{ width: `${weeklyProgress.percentage}%` }}
                        role="progressbar"
                      ></div>
                    </div>
                    <small className="d-block mt-2" style={{ opacity: 0.9 }}>
                      {weeklyProgress.percentage}% complete - Great work!
                    </small>
                  </div>
                </div>
              </div>
                </div>
              )}
            </div>

            {/* Right Section - Quick Stats */}
            {isExpanded && (
            <div className="col-lg-4 animate__animated animate__fadeIn">
              <div className="text-center p-4 rounded-4" 
                style={{ 
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)'
                }}>
                <div className="mb-3">
                  <i className="fas fa-chart-line fa-3x" style={{ opacity: 0.9 }}></i>
                </div>
                <div className="display-4 fw-bold mb-2">
                  {userStats?.overall_progress || 0}%
                </div>
                <div className="h5 mb-3">Overall Progress</div>
                <div className="mb-2">
                  <i className="fas fa-clipboard-check me-2"></i>
                  <span>{userStats?.tests_completed || 0} Tests Completed</span>
                </div>
                <div className="progress mt-3" style={{ height: '6px', background: 'rgba(255, 255, 255, 0.25)' }}>
                  <div 
                    className="progress-bar bg-white" 
                    style={{ width: `${userStats?.overall_progress || 0}%` }}
                  ></div>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroWelcome;
